import * as React from 'react'

import { useFormContext } from 'react-hook-form'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import DynamicFieldRenderer from './DynamicFieldRenderer'
import { DynamicFormProps, DYNAMIC_FORM_ID, FieldConfig } from './types'

// Group consecutive fields that share a non-empty `rowGroup` so they render
// side by side (e.g. icon + color); everything else stays one-per-row.
function groupFieldsForLayout(fields: FieldConfig[]): FieldConfig[][] {
  const groups: FieldConfig[][] = []
  fields.forEach(field => {
    const last = groups[groups.length - 1]
    if (field.rowGroup && last && last[0].rowGroup === field.rowGroup) {
      last.push(field)
    } else {
      groups.push([field])
    }
  })
  return groups
}

export default function DynamicForm({
  fields,
  onSubmit,
  fileUploadFn,
  className,
  submitButtonText = Localization.translator.submit_verb,
  isSubmitting = false,
  hideSubmitButton = false,
  hideHelperText = false,
  formId = DYNAMIC_FORM_ID,
  onFieldsChange,
  noValidate = false,
}: DynamicFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext<Record<string, unknown>>()

  return (
    <form
      id={formId}
      onSubmit={handleSubmit(onSubmit)}
      className={className}
      noValidate={noValidate}
    >
      {groupFieldsForLayout(fields).map(group => {
        const renderField = (field: FieldConfig) => (
          <DynamicFieldRenderer
            key={field.name}
            field={field}
            control={control}
            fileUploadFn={fileUploadFn}
            error={errors[field.name]?.message as string}
            hideHelperText={hideHelperText}
            onFieldChange={onFieldsChange}
          />
        )
        if (group.length === 1) return renderField(group[0])
        return (
          <div key={group[0].rowGroup} className="tw:flex tw:gap-4">
            {group.map(field => (
              <div key={field.name} className="tw:min-w-0 tw:flex-1">
                {renderField(field)}
              </div>
            ))}
          </div>
        )
      })}

      {!hideSubmitButton && (
        <Button
          type="submit"
          variant="default"
          form={formId}
          disabled={isSubmitting}
        >
          {isSubmitting ? Localization.translator.submitting : submitButtonText}
        </Button>
      )}
    </form>
  )
}
